//Dimensão de Conta desabilitada para as métricas de Indicadores porque tem tabelas que não possuem id de conta, se a pessoa configurar uma categoria que 
//usa uma consulta que precisa dessa tabela que naõ tem id de conta, vai quebrar a consulta.
//GGarnize 13/03/2026 11:18
import metricList from "./metricList.json";
import dimensionList from "./dimensionList.json";
import relations from "./erpMetricDimensions.json";

/**
 * =========================================================
 * Constantes públicas
 * =========================================================
 */
export const ERP = { Omie: 0, ContaAzul: 1, Nibo: 2, Excel: 3, ContaAzulV2: 5, Vehro: 7 };
export const GROUPS = { Financeiro: 0, Vendas: 1, DRE: 2, Indicadores: 3, Posicao: 4 };
/** Dimensão "Empresa" (syncs dependentes): disponível apenas para sync type=2 (consolidado) */
export const DIMENSION_EMPRESA_CONSOLIDADA = 26;

const normalizeBaseDateValue = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

export function isExcelErp(erpId) {
  return Number(erpId) === ERP.Excel || normalizeBaseDateValue(erpId) === "excel";
}

export function isPrevisaoMetricIdentifier(metricIdentifier) {
  const metric = metricsByIdentifier[Number(metricIdentifier)];
  if (!metric) return false;
  return normalizeBaseDateValue(metric.base_date) === "previsao";
}

// Métricas de títulos a pagar: a pessoa do título é o fornecedor (nas demais, cliente).
const PAYMENT_METRIC_IDENTIFIERS = new Set([
  9, 10, 11, 12, 63, 64, 65, 66, 67, 68, 69, 99, 108, 109, 110, 112, 114, 132, 143, 145,
]);

export function isPaymentMetricIdentifier(metricIdentifier) {
  return PAYMENT_METRIC_IDENTIFIERS.has(Number(metricIdentifier));
}

const GROUP_LABELS = {
  [GROUPS.Financeiro]: "Financeiro",
  [GROUPS.Vendas]: "Vendas",
  [GROUPS.DRE]: "DRE",
  [GROUPS.Indicadores]: "Indicadores",
  [GROUPS.Posicao]: "Posições",
};

/**
 * =========================================================
 * Índices rápidos
 * =========================================================
 */
const dimsByIdentifier = Object.fromEntries(
  dimensionList.map((d) => [d.identifier, d])
);
const metricsByIdentifier = Object.fromEntries(
  metricList.map((m) => [m.identifier, m])
);
const metricsByGroup = metricList.reduce((acc, m) => {
  (acc[m.group] ||= []).push(m);
  return acc;
}, {});
// ordem estável das dimensões (pela ordem do arquivo)
const dimOrder = Object.fromEntries(dimensionList.map((d, idx) => [d.identifier, idx]));


/**
 * =========================================================
 * Helpers de ERP & Relations
 * =========================================================
 */
function getExcludedGroups(erpId) {
  const normalizedErpId = Number(erpId);
  const hit = (relations.groupOverrides || []).find((o) => Number(o.erp) === normalizedErpId);
  return new Set(hit?.excludeGroups || []);
}

function getOverrides(erpId, metricIdentifier) {
  const normalizedErpId = Number(erpId);
  const normalizedMetricIdentifier = Number(metricIdentifier);
  return (relations.overrides || []).filter(
    (o) =>
      Number(o.erp) === normalizedErpId &&
      (o.metric === "ALL" || Number(o.metric) === normalizedMetricIdentifier)
  );
}

function applyExcludeDims(dimIds, overrides) {
  if (!overrides?.length) return dimIds;
  const excluded = new Set();
  overrides.forEach((o) => (o.excludeDims || []).forEach((id) => excluded.add(id)));
  return dimIds.filter((id) => !excluded.has(id));
}

// Vehro é só financeiro: além do grupo Vendas (groupOverrides), não tem as métricas de clientes, que vêm do faturamento
const VEHRO_UNAVAILABLE_METRICS = new Set([104, 105]);

function isMetricVisibleForErp(metricIdentifier, erpId) {
  const normalizedMetricIdentifier = Number(metricIdentifier);
  const normalizedErpId = Number(erpId);
  const m = metricsByIdentifier[normalizedMetricIdentifier];
  if (!m) return false;
  if (normalizedErpId === ERP.Nibo && normalizedMetricIdentifier === 49) return false;
  if (normalizedErpId === ERP.Vehro && VEHRO_UNAVAILABLE_METRICS.has(normalizedMetricIdentifier)) return false;
  const excludedGroups = getExcludedGroups(normalizedErpId);
  
  // Primeiro verifica se o grupo está excluído
  if (excludedGroups.has(m.group)) return false;
  
  // Exclusividade por ERP (exclusiveMetricPerERP):
  // Métricas listadas como exclusivas de algum ERP só aparecem naquele ERP.
  const exclusiveArr = relations?.exclusiveMetricPerERP || [];
  if (Array.isArray(exclusiveArr) && exclusiveArr.length > 0) {
    const union = new Set();
    exclusiveArr.forEach(e => (e?.metrics || []).forEach(n => { if (typeof n === 'number') union.add(n); }));
    if (union.has(normalizedMetricIdentifier)) {
      const entry = exclusiveArr.find(e => e && Number(e.erp) === normalizedErpId);
      const set = new Set((entry?.metrics || []).filter(n => typeof n === 'number'));
      return set.has(normalizedMetricIdentifier);
    }
  }
  
  // Verifica se há regras específicas para este ERP
  const specificRules = (relations.rules || []).filter(rule => Number(rule.erp) === normalizedErpId);
  if (specificRules.length > 0) {
    // Se há regras específicas para este ERP, só mostra métricas que têm regras
    return specificRules.some(rule => Number(rule.metric) === normalizedMetricIdentifier);
  }
  
  // Se não há regras específicas, usa a lógica padrão
  return true;
}

function ruleForMetric(metricIdentifier) {
  const normalizedMetricIdentifier = Number(metricIdentifier);
  return (relations.rules || []).find((r) => Number(r.metric) === normalizedMetricIdentifier) || null;
}

function expandDimSets(setNames) {
  const out = new Set();
  for (const name of setNames || []) {
    (relations.dimSets[name] || []).forEach((id) => out.add(id));
  }
  return [...out];
}

/**
 * =========================================================
 * API p/ UI
 * =========================================================
 */

/**
 * 1) Árvore de métricas (formato que seu Context/TreeSelect usa)
 * - Grupo com identifier string "g:<id>" (evita colisão com métricas numéricas)
 * - Grupo é `selectable:false`
 * - Métrica mantém `identifier` numérico (casa com enum do C#)
 */
export function buildMetricsTreeForContext(erpId) {
  const excludedGroups = getExcludedGroups(erpId);
  const ABC_METRIC_ID = 57; // Curva ABC não deve aparecer no TreeSelect
  // Saldo projetado (20) e Saldo inicial (28) são exclusivos do componente SaldosWidget
  const SALDOS_WIDGET_ONLY_METRICS = new Set([20, 28]);
  const hidePrevisaoMetrics = isExcelErp(erpId);

  const groups = [...new Set(metricList.map(m => m.group))]
    .filter(g => !excludedGroups.has(g))
    .sort((a, b) => a - b);

  return groups.map(gid => ({
    id: gid,
    name: GROUP_LABELS[gid] || `Grupo ${gid}`,
    icon: null,
    identifier: `g:${gid}`, // valor do grupo no TreeSelect
    selectable: false,
    children: (metricsByGroup[gid] || [])
      .filter(m => isMetricVisibleForErp(m.identifier, erpId)) // Filtra métricas por ERP
      .filter(m => m.identifier !== ABC_METRIC_ID) // Exclui Curva ABC da visualização
      .filter(m => !SALDOS_WIDGET_ONLY_METRICS.has(m.identifier)) // Exclusivos do SaldosWidget
      .filter(m => !(hidePrevisaoMetrics && normalizeBaseDateValue(m.base_date) === "previsao"))
      .map(m => ({
        id: m.id,
        name: m.label,
        icon: m.icon ?? null,
        identifier: m.identifier, // mantém o enum numérico
        format_type: m.format_type ?? null,
        signal: m.signal ?? null,
        read_only: m.read_only ?? false, // FIX: sem tautologia
        selectable: true,
        aggregator: m.aggregator ?? null,
        base_date: m.base_date,
        group: m.group
      }))
      .sort((a, b) => a.id - b.id)
  }));
}

/**
 * 2) MMC de dimensões (interseção) para N métricas
 * @param {number[]} metricIdentifiers  ex.: [0,1,2]
 * @param {number}   erpId              ex.: 0=Omie
 * @param {object}   options            { isDate?: true|false }  (undefined = sem filtro)
 *
 * Retorna no formato do teu <Select>:
 *   { name, label, identifier, is_date, icon }
 */
export function buildDimensionOptionsForMetrics(
  metricIdentifiers,
  erpId,
  options = {}
) {
  const { isDate, syncType } = options; // syncType === 2 => consolidado (dimensão Empresa disponível)

  // filtra inválidos e invisíveis p/ ERP
  const uniq = [
    ...new Set(
      (metricIdentifiers || []).filter(
        (id) => id !== null && id !== undefined && isMetricVisibleForErp(id, erpId)
      )
    ),
  ];
  if (uniq.length === 0) return [];

  // pega conjuntos de dimensões por métrica + aplica overrides (exclusões por ERP)
  const dimSetsPerMetric = uniq.map((id) => {
    const rule = ruleForMetric(id);
    if (!rule) return new Set();
    let dimIds = expandDimSets(rule.dimSets);
    dimIds = applyExcludeDims(dimIds, getOverrides(erpId, id));
    return new Set(dimIds);
  });
  // interseção
  let common = dimSetsPerMetric[0];
  for (let i = 1; i < dimSetsPerMetric.length; i++) {
    common = new Set([...common].filter((x) => dimSetsPerMetric[i].has(x)));
  }

  // filtro simples por isDate (se solicitado)
  let filteredIds = [...common];
  if (isDate === true) {
    filteredIds = filteredIds.filter((id) => dimsByIdentifier[id]?.isDate === true);
  } else if (isDate === false) {
    filteredIds = filteredIds.filter((id) => dimsByIdentifier[id]?.isDate === false);
  }

  // Dimensão Empresa (26): só para sync consolidado (type === 2)
  if (syncType !== 2) {
    filteredIds = filteredIds.filter((id) => id !== DIMENSION_EMPRESA_CONSOLIDADA);
  }
  // monta opções para o <Select>
  const optionsArr = filteredIds
    .map(id => {
      const d = dimsByIdentifier[id];
      if (!d) return null;
      return {
        name: d.label,
        label: d.label,
        identifier: d.identifier,
        is_date: !!d.isDate,
        icon: d.icon ?? null,
        read_only: d.read_only ?? false
      };
    })
    .filter(Boolean)
    .sort((a, b) => (dimOrder[a.identifier] ?? 9999) - (dimOrder[b.identifier] ?? 9999)); // FIX: ordem estável

  return optionsArr;
}

/**
 * 3) (Opcional) Árvore completa (Group -> Metric -> Dims)
 * Usa o mesmo filtro simples por isDate (true/false/undefined)
 */
export function buildTreeData(erpId, options = {}) {
  const metricsTree = buildMetricsTreeForContext(erpId);
  return metricsTree.map(groupNode => ({
    title: groupNode.name,
    value: groupNode.identifier, // "g:<id>"
    key: groupNode.identifier,
    selectable: false,
    children: groupNode.children.map(m => {
      const dimOptions = buildDimensionOptionsForMetrics([m.identifier], erpId, options);
      return {
        title: m.name,
        value: `m:${m.identifier}`,
        key: `m-${m.identifier}`,
        selectable: false,
        children: dimOptions.map(d => ({
          title: d.name,
          value: `m:${m.identifier}|d:${d.identifier}`,
          key: `m-${m.identifier}-d-${d.identifier}`,
          isLeaf: true,
          data: { metricId: m.identifier, dimensionId: d.identifier }
        }))
      };
    })
  }));
}

/**
 * Helpers de rótulo
 */
export const getMetricLabel = (identifier) =>
  metricsByIdentifier[identifier]?.label ?? String(identifier);

export const getDimensionLabel = (identifier) =>
  dimsByIdentifier[identifier]?.label ?? String(identifier);


//Trata metricas de data
export const getMetricBaseDateDimension = (dimension, metric) => {
  if (!metric?.base_date || !dimension?.label) {
    return null;
  }

  // Coluna de data enviada à API (ex.: métricas de “previsão” no UI que usam data_emissao no backend)
  const baseDate = metric.api_base_date ?? metric.base_date;
  const dimensionLabel = dimension.label;

  if (dimensionLabel === "Ano") {
    switch (baseDate) {
      case "Pagamento": return 5;   // Ano de Pagamento
      case "Vencimento": return 8;  // Ano de Vencimento
      case "Emissao": return 11;    // Ano de Emissão
      case "Previsao": return 14;   // Ano de Previsão
      case "Faturamento": return 22;   // Ano de Faturamento
      default: return null;
    }
  } else if (dimensionLabel === "Mes/Ano") {
    switch (baseDate) {
      case "Pagamento": return 6;   // Mes/Ano de Pagamento
      case "Vencimento": return 9;  // Mes/Ano de Vencimento
      case "Emissao": return 12;    // Mes/Ano de Emissão
      case "Previsao": return 15;   // Mes/Ano de Previsão
      case "Faturamento": return 23;   // Mes/Ano de Faturamento
      default: return null;
    }
  } else if (dimensionLabel === "Dia") {
    switch (baseDate) {
      case "Pagamento": return 7;   // Dia de Pagamento
      case "Vencimento": return 10; // Dia de Vencimento
      case "Emissao": return 13;    // Dia de Emissão
      case "Previsao": return 16;   // Dia de Previsão
      case "Faturamento": return 24;   // Dia de Faturamento
      default: return null;
    }
  } else if (dimensionLabel === "Período") {
    return 28; // Período
  }
}

export const getDimensionByIdentifier = (identifier) => {
  return dimensionList.find(d => d.identifier === identifier);
}

export const getMetricByIdentifier = (identifier) => {
  return metricList.find(m => m.identifier === identifier);
}

const OUTFLOW_METRIC_PATTERN = /\b(saidas?|pagamentos?|pago|pagos|pagar|custos?|despesas?|impostos|investimentos|cac)\b/;
const INFLOW_METRIC_PATTERN = /\b(entradas?|recebimentos?|recebidos?|receber|recebiveis|receitas?|clientes|perdas)\b/;

/**
 * Sentido financeiro da métrica: "in" (entradas/recebimentos/vendas), "out" (saídas/pagamentos/custos)
 * ou null quando mistura os dois (geração de caixa, lucro, margens, saldos).
 * Usado para nomear a coluna de pessoa como Cliente (entrada) ou Fornecedor (saída).
 */
export const getMetricCashDirection = (identifier) => {
  const metric = metricsByIdentifier[Number(identifier)];
  if (!metric) return null;
  if (metric.group === GROUPS.Vendas) return "in";
  const label = normalizeBaseDateValue(metric.label);
  const isOut = OUTFLOW_METRIC_PATTERN.test(label);
  const isIn = INFLOW_METRIC_PATTERN.test(label);
  if (isOut === isIn) return null;
  return isOut ? "out" : "in";
}

export const getPersonLabelForMetric = (identifier) => {
  const direction = getMetricCashDirection(identifier);
  if (direction === "in") return "Cliente";
  if (direction === "out") return "Fornecedor";
  return "Cliente/Fornecedor";
}

/**
 * GGARNIZE 13/03/2026 11:18 - Comentário para as métricas que na NIBO não possuem dimensão de Conta, consequentemente sem filtro.
 *   "10 - Pagamento Atrasado",
  "13 - Recebimento em aberto",
  "14 - Recebimento atrasado",
  "17 - Recebimento Atrasado Até 30 Dias",
  "18 - Recebimento Atrasado De 31 A 60 Dias",
  "19 - Recebimento Atrasado De 61 A 90 Dias",
  "20 - Saldo projetado",
  "59 - Recebimento em aberto no período",
  "60 - Recebimento em aberto até 30 dias",
  "62 - Recebimento em aberto de 61 a 90 dias",
  "63 - Pagamento em aberto até 30 dias",
  "64 - Pagamento em aberto de 31 a 60 dias",
  "65 - Pagamento em aberto de 61 a 90 dias",
  "66 - Pagamento atrasado de até 30 dias",
  "67 - Pagamento atrasado de 31 a 60 dias",
  "68 - Pagamento atrasado de 61 a 90 dias",
  "69 - Pagamento em aberto no período",
  "98 - Recebimento atrasado no período",
  "99 - Pagamento atrasado no período",
  "100 - Recebimento em aberto até 07 dias",
  "101 - Recebimento em aberto até 15 dias",
  "102 - Recebimento em aberto de 07 até 15 dias",
  "103 - Recebimento em aberto de 15 até 30 dias",
  "106 - Recebimento Atrasado Previsão",
"107 - Recebimento Atrasado Previsão Período"
 */
